import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { clearSettingsCache, clampItemsPerPage, DEFAULT_ITEMS_PER_PAGE } from '../../utils/settings';
import '../../styles/Forms.css';

// Friendly labels for the auto-cleanup schedule. Value is the cron expression
// stored in the DB and consumed by the backend node-cron scheduler.
const CLEANUP_SCHEDULE_OPTIONS = [
  { value: '0 3 * * *', label: 'Daily at 3:00 AM' },
  { value: '0 0 * * *', label: 'Daily at midnight' },
  { value: '0 */6 * * *', label: 'Every 6 hours' },
  { value: '0 */12 * * *', label: 'Every 12 hours' },
  { value: '0 3 * * 0', label: 'Weekly (Sunday 3:00 AM)' },
];

const GeneralSettingsDetailPage = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [allAdmins, setAllAdmins] = useState([]);
  const [adminPreferences, setAdminPreferences] = useState({});
  const [adminPreferencesPending, setAdminPreferencesPending] = useState({}); // Track unsaved changes
  const [adminSearchTerm, setAdminSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    appName: 'Asset Management',
    companyName: '',
    defaultItemsPerPage: DEFAULT_ITEMS_PER_PAGE,
    enableEmailNotifications: true,
    enableIssueEmails: true,
    timezone: 'Asia/Kolkata',
    dateFormat: 'DD/MM/YYYY',
    checkoutPhotosMandatory: false,
    checkinPhotosMandatory: false,
    fileRetentionDays: 30,
    fileCleanupSchedule: '0 3 * * *',
    // Admin notification preferences
    checkoutNotificationsEnabled: true,
    checkinNotificationsEnabled: true,
    transferNotificationsEnabled: true,
  });

  useEffect(() => {
    fetchUserData();
    fetchSettings();
    fetchAllAdmins();
  }, []);

  const fetchAllAdmins = async () => {
    try {
      const response = await api.getUsers({ role: 'admin', limit: 1000 });
      if (response.success) {
        const admins = response.data || [];
        setAllAdmins(admins);
        
        // Initialize admin preferences state
        const preferences = {};
        admins.forEach(admin => {
          preferences[admin._id || admin.id] = {
            checkoutNotificationsEnabled: admin.checkoutNotificationsEnabled !== undefined 
              ? admin.checkoutNotificationsEnabled 
              : true,
            checkinNotificationsEnabled: admin.checkinNotificationsEnabled !== undefined 
              ? admin.checkinNotificationsEnabled 
              : true,
            transferNotificationsEnabled: admin.transferNotificationsEnabled !== undefined
              ? admin.transferNotificationsEnabled
              : true,
          };
        });
        setAdminPreferences(preferences);
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast.error('Error loading admin list');
    }
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const userId = user.id || user._id;
      
      if (userId) {
        const response = await api.getUser(userId);
        if (response.success) {
          setUserData(response.data);
          // Load notification preferences from user data if admin
          if (response.data.role === 'admin') {
            setFormData(prev => ({
              ...prev,
              checkoutNotificationsEnabled: response.data.checkoutNotificationsEnabled !== undefined 
                ? response.data.checkoutNotificationsEnabled 
                : true,
              checkinNotificationsEnabled: response.data.checkinNotificationsEnabled !== undefined 
                ? response.data.checkinNotificationsEnabled 
                : true,
              transferNotificationsEnabled: response.data.transferNotificationsEnabled !== undefined
                ? response.data.transferNotificationsEnabled
                : true,
            }));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.getSettings();
      if (response.success && response.data) {
        setFormData((prev) => ({
          ...prev,
          appName: response.data.appName || 'Asset Management',
          companyName: response.data.companyName || '',
          defaultItemsPerPage: clampItemsPerPage(response.data.defaultItemsPerPage ?? DEFAULT_ITEMS_PER_PAGE),
          enableEmailNotifications: response.data.enableEmailNotifications !== undefined ? response.data.enableEmailNotifications : true,
          enableIssueEmails: response.data.enableIssueEmails !== undefined ? response.data.enableIssueEmails : true,
          timezone: response.data.timezone || 'Asia/Kolkata',
          dateFormat: response.data.dateFormat || 'DD/MM/YYYY',
          checkoutPhotosMandatory: response.data.checkoutPhotosMandatory !== undefined ? response.data.checkoutPhotosMandatory : false,
          checkinPhotosMandatory: response.data.checkinPhotosMandatory !== undefined ? response.data.checkinPhotosMandatory : false,
          fileRetentionDays: response.data.fileRetentionDays ?? 30,
          fileCleanupSchedule: response.data.fileCleanupSchedule || '0 3 * * *',
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Fallback to local storage if API fails
      try {
        const stored = localStorage.getItem('generalSettings');
        if (stored) {
          const parsed = JSON.parse(stored);
          setFormData((prev) => {
            const merged = { ...prev, ...parsed };
            merged.defaultItemsPerPage = clampItemsPerPage(merged.defaultItemsPerPage);
            return merged;
          });
        }
      } catch (e) {
        // ignore
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAdminPreferenceChange = (adminId, preferenceType, value) => {
    // Only update local state, don't call API
    setAdminPreferencesPending(prev => ({
      ...prev,
      [adminId]: {
        ...prev[adminId],
        [preferenceType]: value,
      },
    }));
  };

  const handleSelectAll = (preferenceType) => {
    const filteredAdmins = allAdmins.filter(admin => {
      if (!adminSearchTerm) return true;
      const search = adminSearchTerm.toLowerCase();
      const name = (admin.name || '').toLowerCase();
      const email = (admin.email || '').toLowerCase();
      return name.includes(search) || email.includes(search);
    });

    const updates = {};
    filteredAdmins.forEach(admin => {
      const adminId = admin._id || admin.id;
      updates[adminId] = {
        ...adminPreferencesPending[adminId],
        [preferenceType]: true,
      };
    });

    setAdminPreferencesPending(prev => ({
      ...prev,
      ...updates,
    }));
  };

  const handleDeselectAll = (preferenceType) => {
    const filteredAdmins = allAdmins.filter(admin => {
      if (!adminSearchTerm) return true;
      const search = adminSearchTerm.toLowerCase();
      const name = (admin.name || '').toLowerCase();
      const email = (admin.email || '').toLowerCase();
      return name.includes(search) || email.includes(search);
    });

    const updates = {};
    filteredAdmins.forEach(admin => {
      const adminId = admin._id || admin.id;
      updates[adminId] = {
        ...adminPreferencesPending[adminId],
        [preferenceType]: false,
      };
    });

    setAdminPreferencesPending(prev => ({
      ...prev,
      ...updates,
    }));
  };

  // Check if all filtered admins have a preference enabled
  const areAllSelected = (preferenceType) => {
    const filteredAdmins = allAdmins.filter(admin => {
      if (!adminSearchTerm) return true;
      const search = adminSearchTerm.toLowerCase();
      const name = (admin.name || '').toLowerCase();
      const email = (admin.email || '').toLowerCase();
      return name.includes(search) || email.includes(search);
    });

    if (filteredAdmins.length === 0) return false;

    return filteredAdmins.every(admin => {
      const adminId = admin._id || admin.id;
      const savedPreferences = adminPreferences[adminId] || {
        checkoutNotificationsEnabled: admin.checkoutNotificationsEnabled !== undefined 
          ? admin.checkoutNotificationsEnabled 
          : true,
        checkinNotificationsEnabled: admin.checkinNotificationsEnabled !== undefined 
          ? admin.checkinNotificationsEnabled 
          : true,
        transferNotificationsEnabled: admin.transferNotificationsEnabled !== undefined
          ? admin.transferNotificationsEnabled
          : true,
      };
      const pendingChanges = adminPreferencesPending[adminId] || {};
      const preferences = {
        ...savedPreferences,
        ...pendingChanges,
      };
      return preferences[preferenceType] === true;
    });
  };

  // Toggle all filtered admins for a preference type
  const handleToggleAll = (preferenceType) => {
    const allSelected = areAllSelected(preferenceType);
    if (allSelected) {
      handleDeselectAll(preferenceType);
    } else {
      handleSelectAll(preferenceType);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      // Save settings to database
      const settingsData = {
        appName: formData.appName,
        companyName: formData.companyName,
        defaultItemsPerPage: clampItemsPerPage(formData.defaultItemsPerPage),
        enableEmailNotifications: formData.enableEmailNotifications,
        enableIssueEmails: formData.enableIssueEmails,
        timezone: formData.timezone,
        dateFormat: formData.dateFormat,
        checkoutPhotosMandatory: formData.checkoutPhotosMandatory,
        checkinPhotosMandatory: formData.checkinPhotosMandatory,
        fileRetentionDays: Math.min(3650, Math.max(1, parseInt(formData.fileRetentionDays, 10) || 30)),
        fileCleanupSchedule: formData.fileCleanupSchedule,
      };

      const settingsResponse = await api.updateSettings(settingsData);
      if (!settingsResponse.success) {
        throw new Error(settingsResponse.message || 'Failed to save settings');
      }
      
      // Also save to localStorage as backup
      localStorage.setItem('generalSettings', JSON.stringify(settingsData));
      
      // Clear settings cache so it refreshes
      clearSettingsCache();
      
      // Save notification preferences to backend if user is admin
      if (userData && userData.role === 'admin') {
        const user = JSON.parse(sessionStorage.getItem('user') || '{}');
        const userId = user.id || user._id || userData._id || userData.id;
        
        // Save current user's preferences
        if (userId) {
          const updateResponse = await api.updateUser(userId, {
            checkoutNotificationsEnabled: formData.checkoutNotificationsEnabled,
            checkinNotificationsEnabled: formData.checkinNotificationsEnabled,
            transferNotificationsEnabled: formData.transferNotificationsEnabled,
          });
          
          if (updateResponse.success) {
            setUserData(updateResponse.data);
          }
        }

        // Save all admin preferences if there are pending changes
        const pendingChanges = Object.keys(adminPreferencesPending);
        if (pendingChanges.length > 0) {
          const updatePromises = pendingChanges.map(adminId => {
            const changes = adminPreferencesPending[adminId];
            return api.updateUser(adminId, changes);
          });

          const results = await Promise.allSettled(updatePromises);
          
          const successful = results.filter(r => r.status === 'fulfilled' && r.value?.success).length;
          const failed = results.length - successful;

          if (successful > 0) {
            // Update the main preferences state with saved changes
            setAdminPreferences(prev => ({
              ...prev,
              ...adminPreferencesPending,
            }));

            // Update admins in the list
            setAllAdmins(prev => prev.map(admin => {
              const adminId = admin._id || admin.id;
              if (adminPreferencesPending[adminId]) {
                return { ...admin, ...adminPreferencesPending[adminId] };
              }
              return admin;
            }));

            // Clear pending changes
            setAdminPreferencesPending({});
          }
          
          if (failed > 0) {
            toast.warning(`${failed} admin preference${failed !== 1 ? 's' : ''} failed to save`);
          }
        }

        toast.success('Settings and notification preferences saved successfully');
      } else {
        toast.success('Settings saved successfully');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="general-settings-detail-page" style={{ padding: '16px' }}>
      <form onSubmit={handleSubmit}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.4fr 1.6fr',
            gap: '20px',
            marginBottom: '20px',
          }}
        >
          {/* Left column - basic app info */}
          <div
            style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <h2
              style={{
                margin: '0 0 16px 0',
                fontSize: '16px',
                fontWeight: 600,
                color: '#2d3748',
              }}
            >
              Application
            </h2>

            <div className="form-group">
              <label htmlFor="appName">Application Name</label>
              <input
                id="appName"
                name="appName"
                type="text"
                value={formData.appName}
                onChange={handleChange}
                placeholder="Shown in header and emails"
              />
            </div>

            <div className="form-group">
              <label htmlFor="companyName">Company / Organization</label>
              <input
                id="companyName"
                name="companyName"
                type="text"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Your company name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="defaultItemsPerPage">Default Items Per Page</label>
              <select
                id="defaultItemsPerPage"
                name="defaultItemsPerPage"
                value={formData.defaultItemsPerPage}
                onChange={handleChange}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="timezone">Timezone</label>
              <input
                id="timezone"
                name="timezone"
                type="text"
                value={formData.timezone}
                onChange={handleChange}
                placeholder="e.g., Asia/Kolkata"
              />
            </div>

          <div className="form-group">
            <label htmlFor="dateFormat">Date Format</label>
            <select
              id="dateFormat"
              name="dateFormat"
              value={formData.dateFormat}
              onChange={handleChange}
            >
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>

          {/* Photo Upload Settings (moved to left side) */}
          <div
            style={{
              background: 'white',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginTop: '16px',
            }}
          >
            <h2
              style={{
                margin: '0 0 16px 0',
                fontSize: '15px',
                fontWeight: 600,
                color: '#2d3748',
              }}
            >
              Photo Upload Settings
            </h2>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '10px',
                justifyContent: 'flex-start',
              }}
            >
              <input
                id="checkoutPhotosMandatory"
                name="checkoutPhotosMandatory"
                type="checkbox"
                checked={formData.checkoutPhotosMandatory}
                onChange={handleChange}
                style={{ marginRight: '6px' }}
              />
              <label
                htmlFor="checkoutPhotosMandatory"
                style={{ margin: 0, fontSize: '13px', color: '#4a5568' }}
              >
                Require photos for checkout
              </label>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '10px',
                justifyContent: 'flex-start',
              }}
            >
              <input
                id="checkinPhotosMandatory"
                name="checkinPhotosMandatory"
                type="checkbox"
                checked={formData.checkinPhotosMandatory}
                onChange={handleChange}
                style={{ marginRight: '6px' }}
              />
              <label
                htmlFor="checkinPhotosMandatory"
                style={{ margin: 0, fontSize: '13px', color: '#4a5568' }}
              >
                Require photos for checkin
              </label>
            </div>

            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#a0aec0' }}>
              When enabled, users must upload at least one photo when checking out or checking in assets.
            </p>
          </div>

          {/* Project File Retention & Auto-Cleanup */}
          <div
            style={{
              background: 'white',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginTop: '16px',
            }}
          >
            <h2
              style={{
                margin: '0 0 16px 0',
                fontSize: '15px',
                fontWeight: 600,
                color: '#2d3748',
              }}
            >
              Project / Task Files Retention Period
            </h2>

            <div className="form-group">
              <label htmlFor="fileRetentionDays">Delete uploaded files after (days)</label>
              <input
                id="fileRetentionDays"
                name="fileRetentionDays"
                type="number"
                min={1}
                max={3650}
                value={formData.fileRetentionDays}
                onChange={handleChange}
                placeholder="30"
              />
            </div>

            {/* <div className="form-group">
              <label htmlFor="fileCleanupSchedule">Cleanup runs</label>
              <select
                id="fileCleanupSchedule"
                name="fileCleanupSchedule"
                value={formData.fileCleanupSchedule}
                onChange={handleChange}
              >
                {!CLEANUP_SCHEDULE_OPTIONS.some((o) => o.value === formData.fileCleanupSchedule) && (
                  <option value={formData.fileCleanupSchedule}>
                    {`Custom (${formData.fileCleanupSchedule})`}
                  </option>
                )}
                {CLEANUP_SCHEDULE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div> */}

            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#a0aec0' }}>
              A background job removes project files older than the retention period.
              Changing the retention period applies to newly uploaded files.
            </p>
          </div>
        </div>

        {/* Right column - notifications */}
        <div>
          <div
            style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginBottom: '16px',
            }}
          >
            <h2
              style={{
                margin: '0 0 16px 0',
                fontSize: '16px',
                fontWeight: 600,
                color: '#2d3748',
              }}
            >
              Email & Notifications
            </h2>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '10px',
                justifyContent: 'flex-start',
              }}
            >
              <input
                id="enableEmailNotifications"
                name="enableEmailNotifications"
                type="checkbox"
                checked={formData.enableEmailNotifications}
                onChange={handleChange}
                style={{ marginRight: '6px' }}
              />
              <label
                htmlFor="enableEmailNotifications"
                style={{ margin: 0, fontSize: '13px', color: '#4a5568' }}
              >
                Enable email notifications
              </label>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '10px',
                justifyContent: 'flex-start',
              }}
            >
              <input
                id="enableIssueEmails"
                name="enableIssueEmails"
                type="checkbox"
                checked={formData.enableIssueEmails}
                onChange={handleChange}
                style={{ marginRight: '6px' }}
              />
              <label
                htmlFor="enableIssueEmails"
                style={{ margin: 0, fontSize: '13px', color: '#4a5568' }}
              >
                Email admin when employee reports an issue
              </label>
            </div>

            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#a0aec0' }}>
              General email notification settings (stored locally).
            </p>
          </div>

          {/* Admin Email Notification Management */}
            {userData && userData.role === 'admin' && allAdmins.length > 0 && (
              <div
                style={{
                  background: 'white',
                  padding: '16px',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  marginTop: '16px',
                }}
              >
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <h2
                      style={{
                        margin: 0,
                        fontSize: '15px',
                        fontWeight: 600,
                        color: '#2d3748',
                      }}
                    >
                      Admin Email Notification Management
                    </h2>
                    <span style={{ 
                      fontSize: '11px', 
                      color: '#718096',
                      background: '#f7fafc',
                      padding: '4px 8px',
                      borderRadius: '4px'
                    }}>
                      {allAdmins.filter(admin => {
                        if (!adminSearchTerm) return true;
                        const search = adminSearchTerm.toLowerCase();
                        const name = (admin.name || '').toLowerCase();
                        const email = (admin.email || '').toLowerCase();
                        return name.includes(search) || email.includes(search);
                      }).length} admin{allAdmins.filter(admin => {
                        if (!adminSearchTerm) return true;
                        const search = adminSearchTerm.toLowerCase();
                        const name = (admin.name || '').toLowerCase();
                        const email = (admin.email || '').toLowerCase();
                        return name.includes(search) || email.includes(search);
                      }).length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  
                  {/* Select All Checkboxes — equal columns */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                      gap: '12px',
                      marginBottom: '8px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                      <input
                        type="checkbox"
                        checked={areAllSelected('checkoutNotificationsEnabled')}
                        onChange={() => handleToggleAll('checkoutNotificationsEnabled')}
                        style={{
                          width: '16px',
                          height: '16px',
                          cursor: 'pointer',
                          accentColor: '#667eea',
                          flexShrink: 0,
                        }}
                      />
                      <label
                        style={{
                          fontSize: '13px',
                          color: '#4a5568',
                          fontWeight: 500,
                          cursor: 'pointer',
                          margin: 0,
                          minWidth: 0,
                        }}
                      >
                        Select All Check-out
                      </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                      <input
                        type="checkbox"
                        checked={areAllSelected('checkinNotificationsEnabled')}
                        onChange={() => handleToggleAll('checkinNotificationsEnabled')}
                        style={{
                          width: '16px',
                          height: '16px',
                          cursor: 'pointer',
                          accentColor: '#667eea',
                          flexShrink: 0,
                        }}
                      />
                      <label
                        style={{
                          fontSize: '13px',
                          color: '#4a5568',
                          fontWeight: 500,
                          cursor: 'pointer',
                          margin: 0,
                          minWidth: 0,
                        }}
                      >
                        Select All Check-in
                      </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                      <input
                        type="checkbox"
                        checked={areAllSelected('transferNotificationsEnabled')}
                        onChange={() => handleToggleAll('transferNotificationsEnabled')}
                        style={{
                          width: '16px',
                          height: '16px',
                          cursor: 'pointer',
                          accentColor: '#667eea',
                          flexShrink: 0,
                        }}
                      />
                      <label
                        style={{
                          fontSize: '13px',
                          color: '#4a5568',
                          fontWeight: 500,
                          cursor: 'pointer',
                          margin: 0,
                          minWidth: 0,
                        }}
                      >
                        Select All Transfer
                      </label>
                    </div>
                  </div>
                  
                  {/* Search Bar */}
                  <input
                    type="text"
                    placeholder="Search admins..."
                    value={adminSearchTerm}
                    onChange={(e) => setAdminSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '12px',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                <div style={{ 
                  maxHeight: '450px', 
                  overflowY: 'auto',
                  paddingRight: '4px'
                }}>
                  {allAdmins
                    .filter(admin => {
                      if (!adminSearchTerm) return true;
                      const search = adminSearchTerm.toLowerCase();
                      const name = (admin.name || '').toLowerCase();
                      const email = (admin.email || '').toLowerCase();
                      return name.includes(search) || email.includes(search);
                    })
                    .map((admin) => {
                      const adminId = admin._id || admin.id;
                      const isCurrentUser = adminId === (userData._id || userData.id);
                      
                      // Get current preferences (pending changes override saved preferences)
                      const savedPreferences = adminPreferences[adminId] || {
                        checkoutNotificationsEnabled: admin.checkoutNotificationsEnabled !== undefined 
                          ? admin.checkoutNotificationsEnabled 
                          : true,
                        checkinNotificationsEnabled: admin.checkinNotificationsEnabled !== undefined 
                          ? admin.checkinNotificationsEnabled 
                          : true,
                        transferNotificationsEnabled: admin.transferNotificationsEnabled !== undefined
                          ? admin.transferNotificationsEnabled
                          : true,
                      };
                      
                      const pendingChanges = adminPreferencesPending[adminId] || {};
                      const preferences = {
                        ...savedPreferences,
                        ...pendingChanges,
                      };
                      
                      const hasPendingChanges = Object.keys(pendingChanges).length > 0;

                      return (
                        <div
                          key={adminId}
                          style={{
                            padding: '10px',
                            marginBottom: '8px',
                            background: isCurrentUser ? '#f0f4ff' : hasPendingChanges ? '#fffbf0' : '#f7fafc',
                            borderRadius: '6px',
                            border: isCurrentUser 
                              ? '1.5px solid #667eea' 
                              : hasPendingChanges 
                                ? '1.5px solid #f6ad55' 
                                : '1px solid #e2e8f0',
                            transition: 'all 0.15s ease',
                            position: 'relative',
                          }}
                          onMouseEnter={(e) => {
                            if (!isCurrentUser) {
                              e.currentTarget.style.background = '#edf2f7';
                              e.currentTarget.style.borderColor = '#cbd5e0';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isCurrentUser) {
                              e.currentTarget.style.background = hasPendingChanges ? '#fffbf0' : '#f7fafc';
                              e.currentTarget.style.borderColor = hasPendingChanges ? '#f6ad55' : '#e2e8f0';
                            }
                          }}
                        >
                          {hasPendingChanges && (
                            <div style={{
                              position: 'absolute',
                              top: '6px',
                              right: '6px',
                              fontSize: '9px',
                              color: '#c05621',
                              background: '#fef5e7',
                              padding: '2px 6px',
                              borderRadius: '10px',
                              fontWeight: 600
                            }}>
                              Unsaved
                            </div>
                          )}
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            marginBottom: '8px'
                          }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '6px',
                                marginBottom: '2px'
                              }}>
                                <div style={{ 
                                  fontSize: '13px', 
                                  fontWeight: 600, 
                                  color: '#2d3748',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {admin.name || 'Admin User'}
                                </div>
                                {isCurrentUser && (
                                  <span style={{ 
                                    fontSize: '10px', 
                                    color: '#667eea', 
                                    fontWeight: 600,
                                    background: '#e0e7ff',
                                    padding: '1px 6px',
                                    borderRadius: '10px',
                                    flexShrink: 0
                                  }}>
                                    You
                                  </span>
                                )}
                              </div>
                              <div style={{ 
                                fontSize: '11px', 
                                color: '#718096',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {admin.email || 'N/A'}
                              </div>
                            </div>
                          </div>

                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                              gap: '8px',
                            }}
                          >
                            {/* Check-out Notifications */}
                            <label
                              style={{
                                minWidth: 0,
                                display: 'flex',
                                alignItems: 'center',
                                padding: '6px 8px',
                                background: 'white',
                                borderRadius: '4px',
                                border: preferences.checkoutNotificationsEnabled 
                                  ? '1.5px solid #667eea' 
                                  : '1px solid #e2e8f0',
                                cursor: saving ? 'not-allowed' : 'pointer',
                                transition: 'all 0.15s ease',
                                opacity: saving ? 0.6 : 1,
                              }}
                              onMouseEnter={(e) => {
                                if (!saving) {
                                  e.currentTarget.style.borderColor = '#667eea';
                                  e.currentTarget.style.background = '#f0f4ff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!saving) {
                                  e.currentTarget.style.borderColor = preferences.checkoutNotificationsEnabled 
                                    ? '#667eea' 
                                    : '#e2e8f0';
                                  e.currentTarget.style.background = 'white';
                                }
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={preferences.checkoutNotificationsEnabled}
                                onChange={(e) => handleAdminPreferenceChange(adminId, 'checkoutNotificationsEnabled', e.target.checked)}
                                disabled={saving}
                                style={{ 
                                  marginRight: '6px', 
                                  width: '16px', 
                                  height: '16px', 
                                  cursor: saving ? 'not-allowed' : 'pointer',
                                  accentColor: '#667eea',
                                  flexShrink: 0
                                }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ 
                                  fontSize: '12px', 
                                  fontWeight: 500, 
                                  color: '#2d3748',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  📤 Check-out
                                </div>
                              </div>
                            </label>

                            {/* Check-in Notifications */}
                            <label
                              style={{
                                minWidth: 0,
                                display: 'flex',
                                alignItems: 'center',
                                padding: '6px 8px',
                                background: 'white',
                                borderRadius: '4px',
                                border: preferences.checkinNotificationsEnabled 
                                  ? '1.5px solid #667eea' 
                                  : '1px solid #e2e8f0',
                                cursor: saving ? 'not-allowed' : 'pointer',
                                transition: 'all 0.15s ease',
                                opacity: saving ? 0.6 : 1,
                              }}
                              onMouseEnter={(e) => {
                                if (!saving) {
                                  e.currentTarget.style.borderColor = '#667eea';
                                  e.currentTarget.style.background = '#f0f4ff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!saving) {
                                  e.currentTarget.style.borderColor = preferences.checkinNotificationsEnabled 
                                    ? '#667eea' 
                                    : '#e2e8f0';
                                  e.currentTarget.style.background = 'white';
                                }
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={preferences.checkinNotificationsEnabled}
                                onChange={(e) => handleAdminPreferenceChange(adminId, 'checkinNotificationsEnabled', e.target.checked)}
                                disabled={saving}
                                style={{ 
                                  marginRight: '6px', 
                                  width: '16px', 
                                  height: '16px', 
                                  cursor: saving ? 'not-allowed' : 'pointer',
                                  accentColor: '#667eea',
                                  flexShrink: 0
                                }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ 
                                  fontSize: '12px', 
                                  fontWeight: 500, 
                                  color: '#2d3748',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  📥 Check-in
                                </div>
                              </div>
                            </label>

                            {/* Transfer Notifications */}
                            <label
                              style={{
                                minWidth: 0,
                                display: 'flex',
                                alignItems: 'center',
                                padding: '6px 8px',
                                background: 'white',
                                borderRadius: '4px',
                                border: preferences.transferNotificationsEnabled
                                  ? '1.5px solid #667eea'
                                  : '1px solid #e2e8f0',
                                cursor: saving ? 'not-allowed' : 'pointer',
                                transition: 'all 0.15s ease',
                                opacity: saving ? 0.6 : 1,
                              }}
                              onMouseEnter={(e) => {
                                if (!saving) {
                                  e.currentTarget.style.borderColor = '#667eea';
                                  e.currentTarget.style.background = '#f0f4ff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!saving) {
                                  e.currentTarget.style.borderColor = preferences.transferNotificationsEnabled
                                    ? '#667eea'
                                    : '#e2e8f0';
                                  e.currentTarget.style.background = 'white';
                                }
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={preferences.transferNotificationsEnabled}
                                onChange={(e) => handleAdminPreferenceChange(adminId, 'transferNotificationsEnabled', e.target.checked)}
                                disabled={saving}
                                style={{
                                  marginRight: '6px',
                                  width: '16px',
                                  height: '16px',
                                  cursor: saving ? 'not-allowed' : 'pointer',
                                  accentColor: '#667eea',
                                  flexShrink: 0,
                                }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  color: '#2d3748',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                                >
                                  🔀 Transfer
                                </div>
                              </div>
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  
                  {allAdmins.filter(admin => {
                    if (!adminSearchTerm) return true;
                    const search = adminSearchTerm.toLowerCase();
                    const name = (admin.name || '').toLowerCase();
                    const email = (admin.email || '').toLowerCase();
                    return name.includes(search) || email.includes(search);
                  }).length === 0 && (
                    <div style={{
                      padding: '30px',
                      textAlign: 'center',
                      color: '#a0aec0',
                      fontSize: '12px'
                    }}>
                      No admins found matching "{adminSearchTerm}"
                    </div>
                  )}
                </div>

                {/* Pending Changes Indicator */}
                {Object.keys(adminPreferencesPending).length > 0 && (
                  <div style={{
                    marginTop: '12px',
                    padding: '8px 12px',
                    background: '#fffbf0',
                    border: '1px solid #f6ad55',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#c05621',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span>⚠️</span>
                    <span>
                      {Object.keys(adminPreferencesPending).length} admin preference{Object.keys(adminPreferencesPending).length !== 1 ? 's' : ''} will be saved when you click "Save Settings"
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            paddingTop: '16px',
            borderTop: '1px solid #e2e8f0',
          }}
        >
          <button
            type="button"
            onClick={() => navigate('/admin/settings')}
            style={{
              padding: '10px 20px',
              background: '#e2e8f0',
              color: '#4a5568',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving...' : `Save Settings${Object.keys(adminPreferencesPending).length > 0 ? ` (${Object.keys(adminPreferencesPending).length} admin${Object.keys(adminPreferencesPending).length !== 1 ? 's' : ''})` : ''}`}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GeneralSettingsDetailPage;


